/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import WysiwygEditor from '../WysiwygEditor';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../Reports.redux';
import { useParams } from '@tanstack/react-router';
import useNavigationWarning from 'hooks/useNavigationWarning';
import api from 'api';
import { Box, Grid, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';

const paramFrom = '/reports/edit-content-block/$contentBlockID';

const EditContentBlock = () => {
    const dispatch = useDispatch(),
        { contentBlockID } = useParams({ from: paramFrom }),
        contentBlock = useSelector((state) => state.reports.contentBlock),
        editorContent = useSelector((state) => state.reports.editorContent),
        templates = useSelector((state) => state.reports.editorTemplates),
        autofill = useSelector((state) => state.reports.autofill),
        firstLoad = useSelector((state) => state.reports.firstLoad),
        editorIsDirty = useSelector((state) => state.reports.editorIsDirty),
        [saving, setSaving] = useState(false),
        [Prompt, setDirty, setClean] = useNavigationWarning();

    const handleChange = (newValue, editor) => {
        dispatch(actions.setState({ editorContent: newValue, editorIsDirty: !firstLoad, firstLoad: false }));
    };

    const handleChangeTitle = (e) => {
        dispatch(actions.setSubState('contentBlock', { string_name: e.target.value }, true));
    };

    const handleChangeAutofillShortcut = (e) => {
        dispatch(actions.setSubState('contentBlock', { autofill_shortcut: e.target.value.replace(/\W/g, '') }, true));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put('/reports/content-blocks', {
                string_id: contentBlock.string_id,
                string_content: editorContent,
                string_name: contentBlock.string_name,
                autofill_shortcut: contentBlock.autofill_shortcut,
            });

            dispatch(actions.setState({ editorIsDirty: false }));
        } catch (err) {
            console.error(err);
        }

        setSaving(false);
    };

    // Load template on initial load
    useEffect(() => {
        dispatch(actions.load_content_block(contentBlockID));
    }, [dispatch, contentBlockID]);

    // Reset state when leaving the page
    useEffect(() => {
        // do nothing
        return () => dispatch(actions.resetAll());
    }, [dispatch]);

    // Set editor dirty state
    useEffect(() => {
        editorIsDirty ? setDirty() : setClean();
    }, [editorIsDirty, setClean, setDirty]);

    // TinyMCE isn't great at updating with new props.
    // Don't render the editor until we've fetched everything.
    if (contentBlock.string_content === undefined) {
        return null;
    }

    return (
        <div>
            <Box sx={{ width: '100%' }}>
                <Grid container rowSpacing={0} columnSpacing={4}>
                    <Grid size={{ xs: 12, md: 9 }}>
                        <TextField
                            label={contentBlock.string_name ? 'Content Block Title' : undefined}
                            placeholder="Content Block Title"
                            value={contentBlock.string_name ?? ''}
                            onChange={handleChangeTitle}
                            sx={{
                                width: '100%',
                                backgroundColor: '#ffffff',
                                '& .MuiInputBase-input': {
                                    fontSize: '1.5rem', // text inside field
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            label={contentBlock.autofill_shortcut ? 'Quick-fill Shortcut' : undefined}
                            placeholder="Quick-fill Shortcut"
                            value={contentBlock.autofill_shortcut ?? ''}
                            onChange={handleChangeAutofillShortcut}
                            sx={{
                                marginBottom: 1,
                                width: '100%',
                                backgroundColor: '#ffffff',
                                '& .MuiInputBase-input': {
                                    fontSize: '1.5rem', // text inside field
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            <WysiwygEditor
                value={editorContent}
                handleSave={handleSave}
                disableSave={saving || !contentBlock.string_name}
                onChange={handleChange}
                templates={templates}
                showReplacementButton={false}
                autofill={autofill}
            />

            {Prompt}
        </div>
    );
};

export default EditContentBlock;
